import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Analytics, track } from '../analytics';
import VideosSection, { VideoIcon } from './VideosSection';
import Icon from './Icon';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SmartCameraSheet from './SmartCameraSheet';
import { usePhotoGroups, photoGroupLabel } from '../hooks/usePhotoGroups';

const MAX_GENERAL = 20;
const MAX_BEFORE_AFTER = 60;

// Тип файла по MIME
function isPdf(file) { return file?.type === 'application/pdf' || file?.filename?.toLowerCase().endsWith('.pdf'); }
function isPdfRecord(p) { return p?.filename?.toLowerCase().endsWith('.pdf') || p?.r2_key?.toLowerCase().endsWith('.pdf'); }

// General photos: aggressive compression (mobile-friendly)
const GENERAL_MAX_WIDTH = 1600;
const GENERAL_QUALITY = 0.75;

// Before/After: high quality for reports
const REPORT_MAX_WIDTH = 2400;
const REPORT_QUALITY = 0.90;

// ─── Image compression via Canvas ───────────────────────────────────────────
// Экспортируется: тем же сжатием пользуется карточка Оборудования.
export async function compressImage(file, maxWidth, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;

      if (width > maxWidth || height > maxWidth) {
        const ratio = Math.min(maxWidth / width, maxWidth / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('Ошибка сжатия')),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Не удалось загрузить изображение')); };
    img.src = url;
  });
}

// ─── Fullscreen viewer with zoom & pan ──────────────────────────────────────
// Экспортируется: тот же просмотрщик открывается из карточки Оборудования.
export function FullscreenViewer({ src, onClose }) {
  const { t } = useTranslation();
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });

  // pinch-to-zoom state
  const lastDist = useRef(null);

  useEffect(() => {
    // Escape — на keyup (см. hooks/useEscape) и в фазе capture с гашением события: родитель
    // (карточка оборудования в режиме «Редактировать») слушает window в фазе bubble и иначе
    // получил бы тот же Escape: просмотрщик закрывался на keydown, а keyup долетал до родителя.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keyup', onKey, true);
    return () => window.removeEventListener('keyup', onKey, true);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const clampOffset = useCallback((ox, oy, sc) => {
    // Allow panning only when zoomed in
    if (sc <= 1) return { x: 0, y: 0 };
    return { x: ox, y: oy };
  }, []);

  // Mouse drag
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragging.current = true;
    dragStart.current = { x: e.clientX - offsetRef.current.x, y: e.clientY - offsetRef.current.y };
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    const nx = e.clientX - dragStart.current.x;
    const ny = e.clientY - dragStart.current.y;
    const clamped = clampOffset(nx, ny, scale);
    offsetRef.current = clamped;
    setOffset(clamped);
  };
  const onMouseUp = () => { dragging.current = false; };

  // Wheel zoom
  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setScale(s => {
      const ns = Math.min(8, Math.max(1, s + delta));
      if (ns <= 1) { offsetRef.current = { x: 0, y: 0 }; setOffset({ x: 0, y: 0 }); }
      return ns;
    });
  };

  // Double-click to reset or zoom to 2×
  const onDblClick = (e) => {
    e.stopPropagation();
    if (scale > 1) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
      offsetRef.current = { x: 0, y: 0 };
    } else {
      setScale(2.5);
    }
  };

  // Touch: pinch zoom + pan
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastDist.current = Math.hypot(dx, dy);
    } else if (e.touches.length === 1 && scale > 1) {
      dragging.current = true;
      dragStart.current = { x: e.touches[0].clientX - offsetRef.current.x, y: e.touches[0].clientY - offsetRef.current.y };
    }
  };
  const onTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 2 && lastDist.current !== null) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / lastDist.current;
      lastDist.current = dist;
      setScale(s => {
        const ns = Math.min(8, Math.max(1, s * ratio));
        if (ns <= 1) { offsetRef.current = { x: 0, y: 0 }; setOffset({ x: 0, y: 0 }); }
        return ns;
      });
    } else if (e.touches.length === 1 && dragging.current) {
      const nx = e.touches[0].clientX - dragStart.current.x;
      const ny = e.touches[0].clientY - dragStart.current.y;
      const clamped = clampOffset(nx, ny, scale);
      offsetRef.current = clamped;
      setOffset(clamped);
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) lastDist.current = null;
    if (e.touches.length === 0) dragging.current = false;
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: '#000',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: scale > 1 ? 'grab' : 'zoom-in',
        touchAction: 'none',
        userSelect: 'none',
      }}
      onPointerDown={e => e.stopPropagation()}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onDoubleClick={onDblClick}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        style={{
          maxWidth: '100vw', maxHeight: '100vh',
          transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
          transformOrigin: 'center center',
          transition: dragging.current ? 'none' : 'transform 0.1s ease',
          pointerEvents: 'none',
          display: 'block',
        }}
      />

      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'fixed', top: 'max(16px, env(safe-area-inset-top, 16px))', right: 'max(16px, env(safe-area-inset-right, 16px))',
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
          color: 'white', fontSize: 20, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(6px)', zIndex: 3001,
        }}
        title={t('photos.closeEsc')}
      >✕</button>

      {/* Zoom hint */}
      {scale === 1 && (
        <div style={{
          position: 'fixed', bottom: 'max(20px, env(safe-area-inset-bottom, 20px))', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.5)', borderRadius: 20,
          padding: '5px 16px', fontSize: 12, color: 'rgba(255,255,255,0.55)',
          backdropFilter: 'blur(4px)', pointerEvents: 'none',
        }}>
          {t('photos.zoomHint')}
        </div>
      )}

      {/* Zoom indicator */}
      {scale > 1 && (
        <div style={{
          position: 'fixed', bottom: 'max(20px, env(safe-area-inset-bottom, 20px))', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.5)', borderRadius: 20,
          padding: '5px 16px', fontSize: 13, color: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(4px)', pointerEvents: 'none',
        }}>
          {Math.round(scale * 100)}% · {t('photos.zoomReset')}
        </div>
      )}
    </div>
  );
}

const CAPTION_MAX = 100;

// ─── Lightbox with navigation ────────────────────────────────────────────────
function Lightbox({ photos, initialIndex, onClose, onDelete, onCaptionSave, disabled, photoGroups, onGroupAssign }) {
  const { t, i18n } = useTranslation();
  const [index, setIndex] = useState(initialIndex);
  const [groupSheetOpen, setGroupSheetOpen] = useState(false);
  const [groupSheetEditing, setGroupSheetEditing] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [caption, setCaption] = useState(photos[initialIndex]?.caption || '');
  const [savingCaption, setSavingCaption] = useState(false);
  const captionDebounce = useRef(null);
  const touchStartX = useRef(null);

  const photo = photos[index];
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;

  // Sync caption when photo changes
  useEffect(() => {
    clearTimeout(captionDebounce.current);
    setCaption(photos[index]?.caption || '');
  }, [index]);

  const goPrev = useCallback((e) => { e && e.stopPropagation(); if (hasPrev) setIndex(i => i - 1); }, [hasPrev]);
  const goNext = useCallback((e) => { e && e.stopPropagation(); if (hasNext) setIndex(i => i + 1); }, [hasNext]);

  const handleCaptionChange = (val) => {
    const trimmed = val.slice(0, CAPTION_MAX);
    setCaption(trimmed);
    // Debounce save: 800ms after last keystroke
    clearTimeout(captionDebounce.current);
    captionDebounce.current = setTimeout(async () => {
      setSavingCaption(true);
      try {
        await onCaptionSave(photo.id, trimmed);
      } finally {
        setSavingCaption(false);
      }
    }, 800);
  };

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (fullscreen) return; // handled by FullscreenViewer
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext, onClose, fullscreen]);

  // Lock body scroll while lightbox is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Прячем Action-FAB, пока открыт лайтбокс/фуллскрин просмотра фото —
  // иначе "+" перекрывает свайп/кнопки навигации по фото.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('fab-hide'));
    return () => window.dispatchEvent(new CustomEvent('fab-show'));
  }, []);

  // Touch swipe + prevent vertical scroll
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchMove = (e) => { e.preventDefault(); };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx < -50) goNext();
    else if (dx > 50) goPrev();
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(t('photos.deleteConfirm'))) return;
    setDeleting(true);
    try {
      await onDelete(photo.id);
      // Stay at same index (next photo shifts in), or go back if last
      const newLen = photos.length - 1;
      if (newLen === 0) { onClose(); return; }
      setIndex(i => Math.min(i, newLen - 1));
      setDeleting(false);
    } catch {
      setDeleting(false);
    }
  };

  const handleGroupPick = async (group) => {
    setSavingGroup(true);
    setGroupSheetOpen(false);
    try { await onGroupAssign(photo.id, group ? group.id : null); }
    finally { setSavingGroup(false); }
  };

  // Новую группу — сразу назначаем этому кадру: юзер открыл шторку именно
  // затем, чтобы разложить ЭТО фото, а не просто завести пустую запись.
  const handleGroupAdd = async (label) => {
    const created = await photoGroups.createGroup(label);
    if (created) await handleGroupPick(created);
  };

  const handleGroupRename = async (groupId, label) => {
    await photoGroups.renameGroup(groupId, label);
  };

  const handleGroupArchive = async (groupId) => {
    await photoGroups.archiveGroup(groupId);
  };

  const arrowStyle = (side, active) => ({
    position: 'fixed',
    top: '50%', transform: 'translateY(-50%)',
    [side]: 12,
    width: 44, height: 44,
    borderRadius: '50%',
    background: active ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.22)',
    color: active ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.2)',
    fontSize: 20, cursor: active ? 'pointer' : 'default',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(4px)',
    transition: 'background 0.15s, color 0.15s',
    zIndex: 2001, userSelect: 'none',
  });

  return (
    <>
      {fullscreen && (
        <FullscreenViewer src={photo.url} onClose={() => setFullscreen(false)} />
      )}

      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          touchAction: 'none',
        }}
        onClick={onClose}
        onPointerDown={e => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Image — click opens fullscreen */}
        <img
          src={photo.url}
          alt=""
          title={t('photos.clickFullscreen')}
          draggable={false}
          style={{
            maxWidth: '100%', maxHeight: '100%', borderRadius: 8,
            boxShadow: '0 4px 32px rgba(0,0,0,0.5)', opacity: deleting ? 0.4 : 1,
            cursor: 'zoom-in',
          }}
          onClick={e => { e.stopPropagation(); setFullscreen(true); }}
        />

        {/* Expand button (top-right of image area) */}
        <button
          onClick={e => { e.stopPropagation(); setFullscreen(true); }}
          style={{
            position: 'fixed', top: 'max(16px, env(safe-area-inset-top, 16px))', right: 'max(16px, env(safe-area-inset-right, 16px))',
            width: 38, height: 38, borderRadius: '50%',
            background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
            color: 'white', fontSize: 16, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(6px)', zIndex: 2002,
          }}
          title={t('photos.fullscreen')}
        >⛶</button>

        {/* Prev arrow */}
        {photos.length > 1 && (
          <button style={arrowStyle('left', hasPrev)} onClick={goPrev} title={t('photos.prev')}>‹</button>
        )}

        {/* Next arrow */}
        {photos.length > 1 && (
          <button style={arrowStyle('right', hasNext)} onClick={goNext} title={t('photos.next')}>›</button>
        )}

        {/* Counter */}
        {photos.length > 1 && (
          <div style={{
            position: 'fixed', top: 'max(16px, env(safe-area-inset-top, 16px))', left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.55)', borderRadius: 20,
            padding: '4px 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)',
            backdropFilter: 'blur(4px)', userSelect: 'none',
          }}>
            {index + 1} / {photos.length}
          </div>
        )}

        {/* Caption input + Bottom controls */}
        <div
          style={{
            position: 'fixed',
            bottom: 'max(32px, env(safe-area-inset-bottom, 32px))',
            left: '50%', transform: 'translateX(-50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
            width: 'min(92vw, 480px)',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Caption field */}
          <div style={{ position: 'relative', width: '100%' }}>
            <textarea
              key={photo.id}
              value={caption}
              onChange={e => handleCaptionChange(e.target.value)}
              placeholder={t('photos.captionPlaceholder')}
              maxLength={CAPTION_MAX}
              rows={2}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: 'rgba(0,0,0,0.45)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 14,
                color: 'white',
                fontSize: 15,
                lineHeight: 1.4,
                padding: '10px 56px 10px 14px',
                resize: 'none',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              onFocus={e => { e.target.style.border = '1px solid rgba(255,255,255,0.45)'; }}
              onBlur={e => { e.target.style.border = '1px solid rgba(255,255,255,0.18)'; }}
            />
            {/* Char counter */}
            <span style={{
              position: 'absolute', bottom: 8, right: 12,
              fontSize: 11, color: caption.length >= CAPTION_MAX ? 'rgba(255,120,120,0.9)' : 'rgba(255,255,255,0.45)',
              userSelect: 'none', pointerEvents: 'none',
              transition: 'color 0.2s',
            }}>
              {savingCaption ? '…' : `${caption.length} ${t('photos.of')} ${CAPTION_MAX}`}
            </span>
          </div>

          {/* Buttons row */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            {photoGroups && (
              <button
                onClick={e => { e.stopPropagation(); setGroupSheetOpen(true); }}
                disabled={savingGroup}
                data-ph-capture="photo_group_pill_open"
                style={{
                  background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white', fontSize: 14, fontWeight: 600, height: 48, paddingLeft: 20, paddingRight: 20,
                  borderRadius: 24, cursor: savingGroup ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center',
                  whiteSpace: 'nowrap', backdropFilter: 'blur(6px)', opacity: savingGroup ? 0.6 : 1,
                }}
              >
                {savingGroup ? '…' : t('photoGroups.pillLabel', {
                  name: photoGroups.groups.find(g => g.id === photo.photo_group_id)
                    ? photoGroupLabel(photoGroups.groups.find(g => g.id === photo.photo_group_id), i18n.language)
                    : t('photoGroups.pillNone'),
                })}
              </button>
            )}
            {!disabled && (
              <button onClick={handleDelete} disabled={deleting} style={{
                background: '#c0392b', border: 'none', color: 'white',
                fontSize: 15, fontWeight: 600, height: 48, paddingLeft: 28, paddingRight: 28,
                borderRadius: 24, cursor: deleting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center',
                whiteSpace: 'nowrap', boxShadow: '0 2px 12px rgba(0,0,0,0.5)', opacity: deleting ? 0.6 : 1,
              }}>{deleting ? '…' : t('photos.delete')}</button>
            )}
            <button onClick={onClose} style={{
              background: 'white', border: 'none', color: '#111',
              fontSize: 15, fontWeight: 600, height: 48, paddingLeft: 32, paddingRight: 32,
              borderRadius: 24, cursor: 'pointer', display: 'flex', alignItems: 'center',
              whiteSpace: 'nowrap', boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
            }}>{t('photos.close')}</button>
          </div>
        </div>

        {/* Внутри фикс-контейнера лайтбокса намеренно: у SmartCameraSheet
            position:absolute/inset:0 — снаружи (сиблингом) он либо рисуется
            ПОД полноэкранным фоном лайтбокса (тот zIndex:2000 выше), либо
            позиционируется от документа, а не от вьюпорта, если страница
            была проскроллена — оба случая выглядят как «кнопка не жмётся»,
            потому что шторка на самом деле открывается, просто невидимо/не
            под пальцем. Баг найден на DEV 2026-09-10. */}
        {groupSheetOpen && photoGroups && (
          // stopPropagation обязателен: шторка теперь ВНУТРИ фикс-контейнера
          // лайтбокса (см. комментарий выше) — без этого клик по чипу/«Готово»
          // всплывает до onClick={onClose} внешнего div и закрывает не только
          // шторку, а весь просмотр фото.
          <div onClick={e => e.stopPropagation()}>
            <SmartCameraSheet
              kind="photoGroup"
              items={photoGroups.groups}
              selectedId={photo.photo_group_id}
              canEdit={photoGroups.canEditGroups}
              labelOf={(g) => photoGroupLabel(g, i18n.language)}
              editing={groupSheetEditing}
              onToggleEdit={() => setGroupSheetEditing((v) => !v)}
              onPick={handleGroupPick}
              onAdd={handleGroupAdd}
              onRename={handleGroupRename}
              onArchive={handleGroupArchive}
              onClose={() => { setGroupSheetOpen(false); setGroupSheetEditing(false); }}
              busy={savingGroup}
              titleKey="photoGroups.sheetTitle"
              pickHintKey="photoGroups.pickHint"
              pickHintReadonlyKey="photoGroups.pickHintReadonly"
            />
          </div>
        )}
      </div>
    </>
  );
}

// ─── Плейсхолдер загружаемого фото ──────────────────────────────────────────
function UploadingThumb({ photo, accentColor }) {
  const borderColor = accentColor || 'var(--border2)';
  return (
    <div style={{
      position: 'relative', width: '100%', paddingBottom: '100%',
      borderRadius: 8, overflow: 'hidden',
      border: `2px solid ${borderColor}44`,
      background: 'var(--bg3)',
    }}>
      {/* Превью из локального ObjectURL (только Android) */}
      {photo.url && (
        <img src={photo.url} alt="" draggable={false} style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', display: 'block', opacity: 0.45,
        }} />
      )}
      {/* Спиннер по центру */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span className="spinner" style={{ width: 24, height: 24 }} />
      </div>
    </div>
  );
}

// ─── Single photo thumbnail ──────────────────────────────────────────────────
function PhotoThumb({ photo, index, photos, onDelete, onCaptionSave, disabled, accentColor, isDragging, onLightboxChange, photoGroups, onGroupAssign, justDraggedRef }) {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const openLightbox = () => { setLightboxOpen(true); onLightboxChange?.(true); };
  const closeLightbox = () => { setLightboxOpen(false); onLightboxChange?.(false); };

  // `index` — порядковый номер СРЕДИ ВСЕХ файлов секции (бейдж в углу,
  // считается по родительскому массиву photos+PDF вперемешку). `photos` тут —
  // уже ОТФИЛЬТРОВАННЫЙ массив без PDF. Если хоть один PDF стоит раньше этого
  // фото, `index` больше не совпадает с позицией в `photos` — Lightbox получал
  // чужую позицию (или вовсе undefined при выходе за границы → белый экран,
  // краша React без ErrorBoundary). Ищем позицию по id, а не по числу.
  const lightboxIndex = photos.findIndex(p => p.id === photo.id);

  const handleDelete = async (photoId) => {
    setDeleting(true);
    try {
      await onDelete(photoId);
    } catch {
      setDeleting(false);
    }
  };

  const borderColor = accentColor || 'var(--border2)';

  return (
    <>
      <div style={{
        position: 'relative', width: '100%', paddingBottom: '100%',
        borderRadius: 8, overflow: 'hidden',
        border: `2px solid ${borderColor}`,
        cursor: isDragging ? 'grabbing' : 'pointer',
        opacity: deleting ? 0.4 : isDragging ? 0.85 : 1,
        transition: 'opacity 0.2s, box-shadow 0.2s',
        boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.35)' : 'none',
      }} onClick={() => !deleting && !disabled && !isDragging && !justDraggedRef?.current && openLightbox()}>
        <img src={photo.url} alt="" draggable={false} onContextMenu={e => e.preventDefault()} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', WebkitTouchCallout: 'none' }} />
        {/* Порядковый номер — левый нижний угол */}
        <div style={{
          position: 'absolute', bottom: 3, left: 3,
          minWidth: 16, height: 16, borderRadius: 5,
          background: 'rgba(0,0,0,0.52)',
          color: 'rgba(255,255,255,0.85)',
          fontSize: 10, fontWeight: 700, lineHeight: '16px',
          textAlign: 'center', padding: '0 4px',
          pointerEvents: 'none',
          userSelect: 'none',
          backdropFilter: 'blur(2px)',
        }}>
          {index + 1}
        </div>

      </div>

      {lightboxOpen && (
        <Lightbox
          photos={photos}
          initialIndex={lightboxIndex >= 0 ? lightboxIndex : 0}
          onClose={closeLightbox}
          onDelete={handleDelete}
          onCaptionSave={onCaptionSave}
          disabled={disabled}
          photoGroups={photoGroups}
          onGroupAssign={onGroupAssign}
        />
      )}
    </>
  );
}

// ─── Sortable wrapper для PhotoThumb ─────────────────────────────────────────
function SortablePhotoThumb({ photo, index, photos, onDelete, onCaptionSave, disabled, accentColor, onLightboxChange, editMode, onLongPressStart, onLongPressEnd, photoGroups, onGroupAssign, justDraggedRef }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.id });
  const [deleting, setDeleting] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative',
  };

  const handleDeleteClick = async (e) => {
    e.stopPropagation();
    if (!window.confirm(t('photos.deleteConfirm'))) return;
    setDeleting(true);
    try { await onDelete(photo.id); } catch { setDeleting(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={setNodeRef}
        style={{ ...style, WebkitTouchCallout: 'none', userSelect: 'none', touchAction: editMode ? 'none' : 'auto' }}
        {...attributes}
        // onPointerDown/Up/Leave идут ПЕРЕД спредом listeners намеренно: при
        // повторении одного и того же props-ключа в JSX побеждает последний
        // источник. Раньше listeners стоял первым — в editMode dnd-kit'овский
        // onPointerDown (то, что физически запускает драг) затирался этим же
        // ключом от onLongPressStart, который в editMode — no-op (см. его
        // тело: if (editMode) return). На тач это не било, потому что там
        // драг стартует через onTouchStart (TouchSensor) — этот ключ никто не
        // трогал; на мыши единственный активатор — onPointerDown, и он был
        // мёртв. Баг найден на DEV 2026-09-10 (десктоп: драг не работал вовсе).
        onPointerDown={onLongPressStart}
        onPointerUp={onLongPressEnd}
        onPointerLeave={onLongPressEnd}
        {...(editMode ? listeners : {})}
        className={editMode && !isDragging ? 'photo-wiggle' : ''}
      >
        <PhotoThumb
          photo={photo}
          index={index}
          photos={photos}
          onDelete={onDelete}
          onCaptionSave={onCaptionSave}
          disabled={disabled || editMode}
          onLightboxChange={onLightboxChange}
          accentColor={accentColor}
          isDragging={isDragging}
          photoGroups={photoGroups}
          onGroupAssign={onGroupAssign}
          justDraggedRef={justDraggedRef}
        />
      </div>
      {editMode && !disabled && !deleting && (
        <button
          onPointerDown={e => e.stopPropagation()}
          onClick={handleDeleteClick}
          style={{
            position: 'absolute', top: 2, right: 2, zIndex: 20,
            width: 20, height: 20, borderRadius: '50%',
            background: 'rgba(0,0,0,0.65)', border: 'none',
            color: 'white', fontSize: 13, lineHeight: 1,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0,
          }}
        >×</button>
      )}
    </div>
  );
}

// ─── PDF icon fallback (SVG) ──────────────────────────────────────────────────
function PdfIcon({ shortName }) {
  return (
    <div style={{
      position: 'absolute', inset: 0,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 4, padding: 6,
      background: 'var(--bg2)',
    }}>
      <svg width="32" height="38" viewBox="0 0 32 38" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="32" height="38" rx="4" fill="#E53935" opacity="0.12"/>
        <path d="M5 0h16l11 11v23a3 3 0 01-3 3H5a3 3 0 01-3-3V3a3 3 0 013-3z" fill="#E53935" opacity="0.85"/>
        <path d="M21 0l11 11H24a3 3 0 01-3-3V0z" fill="#B71C1C" opacity="0.7"/>
        <text x="16" y="27" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="sans-serif">PDF</text>
      </svg>
      <span style={{
        fontSize: 9, color: 'var(--text2)', textAlign: 'center',
        lineHeight: 1.2, maxWidth: '100%',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{shortName}</span>
    </div>
  );
}

// ─── PDF canvas preview (lazy-loads pdfjs-dist) ───────────────────────────────
function PdfPreviewCanvas({ url }) {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'error'

  useEffect(() => {
    let cancelled = false;
    let pdfDoc = null;

    async function render() {
      try {
        // Динамический импорт — грузится только при наличии PDF
        const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
        // Worker в public/ — грузится браузером напрямую, не через webpack
        pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

        pdfDoc = await pdfjsLib.getDocument({ url, cMapPacked: true }).promise;
        if (cancelled) return;

        const page = await pdfDoc.getPage(1);
        if (cancelled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        // Масштабируем под размер canvas (квадрат ~160px)
        const SIZE = 160;
        const viewport = page.getViewport({ scale: 1 });
        const scale = SIZE / Math.max(viewport.width, viewport.height);
        const scaled = page.getViewport({ scale });

        canvas.width = scaled.width;
        canvas.height = scaled.height;
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport: scaled }).promise;

        if (!cancelled) setStatus('ok');
      } catch {
        if (!cancelled) setStatus('error');
      }
    }

    render();
    return () => {
      cancelled = true;
      pdfDoc?.destroy?.();
    };
  }, [url]);

  if (status === 'error') return null; // fallback рендерится выше

  return (
    // ph-no-capture: PDF из R2 (cross-origin) делает canvas tainted →
    // rrweb.toDataURL() кидает SecurityError и ломает весь снапшот на iOS.
    // Блокируем узел от записи — плеер показывает плейсхолдер, слепок цел.
    <div className="ph-no-capture" style={{
      position: 'absolute', inset: 0,
      display: status === 'ok' ? 'flex' : 'none',
      alignItems: 'center', justifyContent: 'center',
      background: 'white',
      padding: 6,
      boxSizing: 'border-box',
    }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%', height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </div>
  );
}

// ─── PDF Thumbnail ────────────────────────────────────────────────────────────
// Экспортируется: те же миниатюры PDF стоят в блоке «Договор» карточки Проекта
// (ProjectContractsBlock) — загрузка там повторяет «Служебную информацию».
export function PdfThumb({ photo, index, onDelete, disabled, accentColor, justDraggedRef }) {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);
  const [previewOk, setPreviewOk] = useState(false);
  const borderColor = accentColor || 'var(--border2)';

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(t('photos.documentDeleteConfirm'))) return;
    setDeleting(true);
    try { await onDelete(photo.id); } catch { setDeleting(false); }
  };

  const openPdf = () => { window.open(photo.url, '_blank', 'noopener,noreferrer'); };

  const name = photo.filename || photo.r2_key?.split('/').pop() || 'document.pdf';
  const shortName = name.length > 18 ? name.slice(0, 15) + '…' : name;

  return (
    <div
      style={{
        position: 'relative', width: '100%', paddingBottom: '100%',
        borderRadius: 8, overflow: 'hidden',
        border: `2px solid ${borderColor}`,
        cursor: deleting ? 'default' : 'pointer',
        opacity: deleting ? 0.4 : 1,
        transition: 'opacity 0.2s',
      }}
      onClick={() => !deleting && !disabled && !justDraggedRef?.current && openPdf()}
    >
      {/* Иконка — всегда под canvas, служит fallback */}
      <PdfIcon shortName={shortName} />

      {/* Canvas превью поверх иконки — рендерится через pdf.js */}
      {photo.url && (
        <PdfPreviewCanvas
          url={photo.url}
          onRenderOk={() => setPreviewOk(true)}
        />
      )}

      {/* Имя файла — нижняя полоска */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'rgba(0,0,0,0.52)',
        backdropFilter: 'blur(3px)',
        padding: '3px 22px 3px 6px',
        pointerEvents: 'none', userSelect: 'none',
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <span style={{
          fontSize: 9, color: 'rgba(255,255,255,0.9)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          flex: 1,
        }}>{shortName}</span>
      </div>

      {/* Порядковый номер */}
      <div style={{
        position: 'absolute', bottom: 3, right: 3,
        minWidth: 16, height: 16, borderRadius: 5,
        background: 'rgba(0,0,0,0.45)',
        color: 'rgba(255,255,255,0.85)',
        fontSize: 10, fontWeight: 700, lineHeight: '16px',
        textAlign: 'center', padding: '0 4px',
        pointerEvents: 'none', userSelect: 'none',
      }}>{index + 1}</div>


    </div>
  );
}

// ─── Sortable wrapper для PdfThumb ───────────────────────────────────────────
function SortablePdfThumb({ photo, index, onDelete, disabled, accentColor, editMode, onLongPressStart, onLongPressEnd, justDraggedRef }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.id });
  const [deleting, setDeleting] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative',
    opacity: isDragging ? 0.85 : 1,
  };

  const handleDeleteClick = async (e) => {
    e.stopPropagation();
    if (!window.confirm(t('photos.documentDeleteConfirm'))) return;
    setDeleting(true);
    try { await onDelete(photo.id); } catch { setDeleting(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={setNodeRef}
        style={{ ...style, WebkitTouchCallout: 'none', userSelect: 'none', touchAction: editMode ? 'none' : 'auto' }}
        {...attributes}
        // onPointerDown/Up/Leave идут ПЕРЕД спредом listeners намеренно: при
        // повторении одного и того же props-ключа в JSX побеждает последний
        // источник. Раньше listeners стоял первым — в editMode dnd-kit'овский
        // onPointerDown (то, что физически запускает драг) затирался этим же
        // ключом от onLongPressStart, который в editMode — no-op (см. его
        // тело: if (editMode) return). На тач это не било, потому что там
        // драг стартует через onTouchStart (TouchSensor) — этот ключ никто не
        // трогал; на мыши единственный активатор — onPointerDown, и он был
        // мёртв. Баг найден на DEV 2026-09-10 (десктоп: драг не работал вовсе).
        onPointerDown={onLongPressStart}
        onPointerUp={onLongPressEnd}
        onPointerLeave={onLongPressEnd}
        {...(editMode ? listeners : {})}
        className={editMode && !isDragging ? 'photo-wiggle' : ''}
      >
        <PdfThumb
          photo={photo}
          index={index}
          onDelete={onDelete}
          disabled={disabled || editMode}
          accentColor={accentColor}
          justDraggedRef={justDraggedRef}
        />
      </div>
      {editMode && !disabled && !deleting && (
        <button
          onPointerDown={e => e.stopPropagation()}
          onClick={handleDeleteClick}
          style={{
            position: 'absolute', top: 2, right: 2, zIndex: 20,
            width: 20, height: 20, borderRadius: '50%',
            background: 'rgba(0,0,0,0.65)', border: 'none',
            color: 'white', fontSize: 13, lineHeight: 1,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0,
          }}
        >×</button>
      )}
    </div>
  );
}

// Определяем Android один раз при загрузке модуля
const isAndroid = /android/i.test(navigator.userAgent);

// Сколько ждём реакции системы на открытие пикера, прежде чем признать, что он
// не стартовал. Только Android: на iOS пикер — это шит внутри приложения,
// visibilitychange не стреляет, и сторож дал бы ложную тревогу.
const PICKER_WATCHDOG_MS = 20000;

// ─── Upload slot ─────────────────────────────────────────────────────────────
// Экспортируется: этот же слот стоит в карточке Оборудования. Копию не заводим —
// здесь отлажены сторож пикера на Android, двойной onChange на iOS и
// синхронный input.click(), и вторая копия немедленно начнёт от них отставать.
// Word (.docx) — только там, где слот просят явным acceptWord (пока: Договор Проекта).
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const isDocxFile = (f) => f.type === DOCX_MIME || f.name.toLowerCase().endsWith('.docx');

export function UploadSlot({ onFiles, uploading, multiple = true, accentColor, acceptPdf = false, acceptWord = false, fill = false, compact = false }) {
  const { t } = useTranslation();
  const cameraRef  = useRef();
  const galleryRef = useRef();
  const filesRef   = useRef();
  const [sheet, setSheet] = useState(false);
  const color = accentColor || 'var(--border2)';
  // Защита от двойного срабатывания onChange на iOS
  const handlingRef = useRef(false);

  // Ref на актуальный onFiles — чтобы visibilitychange listener не держал stale closure
  const onFilesRef = useRef(onFiles);
  useEffect(() => { onFilesRef.current = onFiles; }, [onFiles]);

  // Ref на текущий visibilitychange listener — чтобы снять его перед новым открытием
  const visibilityCleanup = useRef(null);

  const cleanupVisibility = useCallback(() => {
    if (visibilityCleanup.current) {
      document.removeEventListener('visibilitychange', visibilityCleanup.current);
      visibilityCleanup.current = null;
    }
  }, []);

  // ── Сторож пикера ────────────────────────────────────────────────────────
  // Если после input.click() приложение не ушло в фон и файлы не пришли —
  // системный пикер не открылся вообще (Chrome отказал в user activation либо
  // нет обработчика интента). Молчаливый провал → событие в PostHog + подсказка.
  const watchdog = useRef(null);

  const disarmWatchdog = useCallback(() => {
    if (!watchdog.current) return;
    clearTimeout(watchdog.current.timer);
    document.removeEventListener('visibilitychange', watchdog.current.onVis);
    watchdog.current = null;
  }, []);

  const armWatchdog = useCallback((source) => {
    if (!isAndroid) return;
    disarmWatchdog();
    const state = { sawBackground: false };
    const onVis = () => { if (document.visibilityState === 'hidden') state.sawBackground = true; };
    document.addEventListener('visibilitychange', onVis);
    const timer = setTimeout(() => {
      document.removeEventListener('visibilitychange', onVis);
      watchdog.current = null;
      // Уходили в фон — пикер стартовал, юзер просто отменил выбор. Это норма.
      if (state.sawBackground) return;
      Analytics.photoPickerNoResponse(source);
      toast.error(t('photos.pickerNoResponse'), { duration: 8000 });
    }, PICKER_WATCHDOG_MS);
    watchdog.current = { timer, onVis };
  }, [disarmWatchdog, t]);

  // Снимаем сторож при размонтировании — иначе toast прилетит на другом экране
  useEffect(() => disarmWatchdog, [disarmWatchdog]);

  // Обработчик для галереи и PDF — onChange работает стабильно (нет перехода в другое приложение)
  const handleGallery = useCallback((e) => {
    disarmWatchdog();
    if (handlingRef.current) return;
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    handlingRef.current = true;
    Analytics.photoPickerFilesReceived('gallery', files.length);
    onFiles(files);
    setTimeout(() => {
      try { e.target.value = ''; } catch {}
      handlingRef.current = false;
    }, 500);
  }, [onFiles, disarmWatchdog]);

  const handleFiles = useCallback((e) => {
    disarmWatchdog();
    if (handlingRef.current) return;
    const allFiles = Array.from(e.target.files || []);
    if (!allFiles.length) return;
    // На iOS accept=undefined — фильтруем вручную
    const files = allFiles.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf') || (acceptWord && isDocxFile(f)));
    if (!files.length) {
      toast.error(t(acceptWord ? 'photos.selectPdfOrWord' : 'photos.selectPdf'));
      return;
    }
    handlingRef.current = true;
    Analytics.photoPickerFilesReceived('files', files.length);
    onFiles(files);
    setTimeout(() => {
      try { e.target.value = ''; } catch {}
      handlingRef.current = false;
    }, 500);
  }, [onFiles, disarmWatchdog, t, acceptWord]);

  // ВАЖНО: input.click() вызывается СИНХРОННО из обработчика клика по кнопке.
  // Через setTimeout Chrome теряет transient user activation и молча отказывает
  // в показе пикера («File chooser dialog can only be shown with a user
  // activation») — ни камера, ни галерея не открывались на Android.
  // setSheet(false) идёт после клика: React батчит стейт, на порядок не влияет.
  const openCamera = useCallback(() => {
    const input = cameraRef.current;
    if (!input) { setSheet(false); return; }

    // Снимаем предыдущий listener если остался
    cleanupVisibility();

    let handled = false;

    const onChangeFn = () => {
      if (handled) return;
      handled = true;
      cleanupVisibility();
      disarmWatchdog();
      const files = Array.from(input.files || []);
      if (files.length) {
        Analytics.photoPickerFilesReceived('camera', files.length);
        onFilesRef.current(files);
      }
      setTimeout(() => { try { input.value = ''; } catch {} }, 300);
    };

    const onVisibilityFn = () => {
      if (document.visibilityState !== 'visible') return;
      cleanupVisibility();
      setTimeout(() => {
        if (!handled) {
          handled = true;
          disarmWatchdog();
          const files = Array.from(input.files || []);
          if (files.length) {
            Analytics.photoPickerFilesReceived('camera', files.length);
            onFilesRef.current(files);
          }
          setTimeout(() => { try { input.value = ''; } catch {} }, 300);
        }
      }, 300);
    };

    visibilityCleanup.current = onVisibilityFn;
    input.addEventListener('change', onChangeFn, { once: true });
    document.addEventListener('visibilitychange', onVisibilityFn);

    Analytics.photoPickerSourceClicked('camera');
    input.value = '';
    input.click();
    armWatchdog('camera');
    setSheet(false);
  }, [cleanupVisibility, armWatchdog, disarmWatchdog]);

  const openGallery = useCallback(() => {
    // Снимаем camera listener если пользователь переключился с камеры на галерею
    cleanupVisibility();
    const input = galleryRef.current;
    if (!input) { setSheet(false); return; }
    Analytics.photoPickerSourceClicked('gallery');
    input.value = '';
    input.click();
    armWatchdog('gallery');
    setSheet(false);
  }, [armWatchdog, cleanupVisibility]);

  const openFiles = useCallback(() => {
    cleanupVisibility();
    const input = filesRef.current;
    if (!input) { setSheet(false); return; }
    Analytics.photoPickerSourceClicked('files');
    input.value = '';
    input.click();
    armWatchdog('files');
    setSheet(false);
  }, [armWatchdog, cleanupVisibility]);

  const handleClick = () => {
    if (uploading) return;
    Analytics.photoPickerOpened(isAndroid ? 'android' : 'other', acceptPdf);
    if (isAndroid) {
      setSheet(true);
    } else {
      // iOS/Desktop: системный пикер сам предложит камеру/галерею/файлы
      Analytics.photoPickerSourceClicked('gallery');
      galleryRef.current?.click();
    }
  };

  // ${color}NN (hex-alpha suffix прямо на литерал) работал только если
  // accentColor — настоящий #rrggbb. Все вызовы этого компонента передают
  // accentColor="var(--text3)" / "var(--green)" и т.п. — CSS-переменную, а
  // "var(--x)55" синтаксически невалиден и браузер тихо роняет ВЕСЬ border
  // (без ошибки в консоли). Отсюда «нет рамки у Добавить», хотя border в
  // коде как бы был. color-mix() работает одинаково и для #hex, и для var().
  // Найдено на DEV 2026-09-10.
  const borderColor = accentColor ? `color-mix(in srgb, ${color} 45%, transparent)` : color;
  const borderColorHover = accentColor ? `color-mix(in srgb, ${color} 75%, transparent)` : color;
  const bgIdle = accentColor ? `color-mix(in srgb, ${color} 8%, transparent)` : 'var(--bg3)';
  const bgHover = accentColor ? `color-mix(in srgb, ${color} 15%, transparent)` : 'var(--bg3)';

  return (
    <>
      <div
        onClick={handleClick}
        style={compact ? {
          // compact: инлайн-кнопка внутри узкой дроп-зоны пустой группы —
          // без своего aspect-ratio, встаёт в строку рядом с текстом хинта.
          display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
          padding: '3px 9px', borderRadius: 10,
          border: `1.5px dashed ${borderColor}`,
          background: bgIdle,
          cursor: uploading ? 'not-allowed' : 'pointer',
          opacity: uploading ? 0.6 : 1,
          transition: 'border-color 0.18s, background 0.18s',
        } : {
          // fill: слот встроен в родителя с уже готовой квадратной геометрией
          // (обёртка position:relative + paddingTop:100%, приём из EquipmentModal) —
          // тогда сам слот просто растягивается на 100%, без своего aspect-ratio.
          // Без fill (грид фото Объекта/Работы) — по старинке ширина + aspect-ratio.
          ...(fill
            ? { position: 'absolute', inset: 0 }
            : { width: '100%', aspectRatio: '1 / 1' }),
          border: `1.5px dashed ${borderColor}`,
          borderRadius: 8, flexShrink: 0, display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          cursor: uploading ? 'not-allowed' : 'pointer',
          background: bgIdle,
          transition: 'border-color 0.18s, background 0.18s',
          opacity: uploading ? 0.6 : 1, gap: 4, overflow: 'hidden',
        }}
        onMouseEnter={e => { if (!uploading) { e.currentTarget.style.borderColor = borderColorHover; e.currentTarget.style.background = bgHover; } }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.background = bgIdle; }}
      >
        <span style={{ fontSize: compact ? 13 : 20, opacity: uploading ? 0.35 : 1 }}>{acceptPdf ? '📎' : '📷'}</span>
        <span style={{ fontSize: compact ? 11 : 10, fontWeight: compact ? 600 : 400, color: accentColor || 'var(--text3)', textAlign: 'center', lineHeight: 1.2, opacity: uploading ? 0.35 : 1 }}>{t('photos.add')}</span>
      </div>

      {/* Камера: capture="environment", onChange регистрируется динамически в openCamera */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} />
      {/* Галерея: android/allowCamera возвращает кнопку камеры на Android 14/15
          На iOS с acceptPdf: добавляем application/pdf чтобы системный пикер показывал файлы */}
      <input ref={galleryRef} type="file"
        accept={isAndroid ? 'image/*' : (acceptPdf ? `image/*,application/pdf${acceptWord ? `,${DOCX_MIME},.docx` : ''}` : 'image/*')}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={handleGallery}
      />
      {/* Файлы (PDF) — только для Служебной информации.
          Только .pdf без image/* — иначе Android Chrome открывает медиапикер вместо файлового менеджера */}
      {acceptPdf && (
        <input ref={filesRef} type="file"
          accept={isAndroid ? `application/pdf,.pdf${acceptWord ? `,${DOCX_MIME},.docx` : ''}` : undefined}
          multiple={multiple}
          style={{ display: 'none' }}
          onChange={handleFiles}
        />
      )}

      {/* Bottomsheet для Android */}
      {sheet && (
        <div
          onClick={() => setSheet(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 10010, // выше демо-спотлайта (1900/10006), иначе шторка уходит под затемнение гайда на Android
            background: 'rgba(0,0,0,0.55)', display: 'flex',
            alignItems: 'flex-end', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#ffffff', borderRadius: '16px 16px 0 0',
              padding: '16px 0 env(safe-area-inset-bottom, 24px)', width: '100%', maxWidth: 480,
              boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{ width: 36, height: 4, borderRadius: 2, background: '#e0e0e0', margin: '0 auto 16px' }} />

            <button onClick={openCamera} style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '14px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#111111' }}>
              <span style={{ fontSize: 22 }}>📷</span>
              {t('photos.camera')}
            </button>

            <button onClick={openGallery} style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '14px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#111111' }}>
              <span style={{ fontSize: 22 }}>🖼️</span>
              {t('photos.gallery')}
            </button>

            {acceptPdf && (
              <button onClick={openFiles} style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '14px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#111111' }}>
                <span style={{ fontSize: 22 }}>📁</span>
                {t('photos.files')}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Report photo gallery (Before / After) с drag-and-drop ──────────────────
// ─── DragOverlay «призрак» перетаскиваемого элемента — общий для Flat/Grouped
function PhotoDragOverlayGhost({ photo, accentColor }) {
  if (!photo) return null;
  return (
    <div style={{
      width: 80, height: 80, borderRadius: 8, overflow: 'hidden',
      boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
      border: `2px solid ${accentColor || 'var(--accent)'}`,
      transform: 'rotate(2deg)',
      background: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {isPdfRecord(photo) ? (
        <svg width="28" height="34" viewBox="0 0 32 38" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 0h16l11 11v23a3 3 0 01-3 3H5a3 3 0 01-3-3V3a3 3 0 013-3z" fill="#E53935" opacity="0.85"/>
          <path d="M21 0l11 11H24a3 3 0 01-3-3V0z" fill="#B71C1C" opacity="0.7"/>
          <text x="16" y="27" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="sans-serif">PDF</text>
        </svg>
      ) : (
        <img src={photo.url} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
    </div>
  );
}

// ─── Плоский грид — ТЕКУЩЕЕ поведение, 1:1, без единой правки. Рисуется, пока
// на Объекте не настроено ни одной Группы (подавляющее большинство Работ).
function FlatPhotoGrid({ photos, setPhotos, onUpload, onDelete, onCaptionSave, uploading, onReorder, acceptPdf, twoCol, editMode, onEditModeChange, showAdd, canAdd, accentColor, photoGroups, onGroupAssign }) {
  const [activePhoto, setActivePhoto] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const longPressTimer = useRef(null);
  // true короткое окно сразу после отпускания драга — гасит следующий synthetic
  // click (mousedown→mousemove→mouseup всё равно рождает click, даже если между
  // ними был JS-драг; isDragging из useSortable к этому моменту уже false).
  // Баг найден на DEV 2026-09-10: после драга открывался Lightbox/PDF.
  const justDraggedRef = useRef(false);

  const startLongPress = () => {
    if (editMode) return;
    if (lightboxOpen) return;
    longPressTimer.current = setTimeout(() => {
      onEditModeChange?.(true);
      if (navigator.vibrate) navigator.vibrate(40);
    }, 400);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: editMode ? 5 : (lightboxOpen ? 99999 : 99999) },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: editMode ? 0 : (lightboxOpen ? 99999 : 250), tolerance: editMode ? 5 : 8 },
    })
  );

  const handleDragStart = ({ active }) => {
    setActivePhoto(photos.find(p => p.id === active.id) || null);
    justDraggedRef.current = true;
  };

  const handleDragEnd = ({ active, over }) => {
    setActivePhoto(null);
    if (!over || active.id === over.id) return;

    const oldIndex = photos.findIndex(p => p.id === active.id);
    const newIndex = photos.findIndex(p => p.id === over.id);
    const reordered = arrayMove(photos, oldIndex, newIndex);

    setPhotos(reordered);

    // Сохранить новый порядок на бэке
    const order = reordered.map((p, i) => ({ id: p.id, display_order: i }));
    onReorder(order);
    Analytics.photoReordered(reordered[0]?.photo_type, reordered.length);
    // Сброс с задержкой 0 — ПОСЛЕ синхронного click, который браузер всё
    // равно диспатчит следом за mouseup, завершившим драг.
    setTimeout(() => { justDraggedRef.current = false; }, 0);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={photos.map(p => p.id)} strategy={rectSortingStrategy}>
        <div className={`photo-grid${twoCol ? ' photo-grid--before-after' : ''}`} style={{ display: 'grid', gap: 8 }}>
          {(() => {
            let realIndex = 0;
            return photos.map((p) => {
              if (p.uploading) {
                return (
                  <div key={p.id}>
                    <UploadingThumb photo={p} accentColor={accentColor} />
                  </div>
                );
              }
              const idx = realIndex++;
              return isPdfRecord(p) ? (
                <SortablePdfThumb
                  key={p.id}
                  photo={p}
                  index={idx}
                  onDelete={onDelete}
                  disabled={uploading}
                  accentColor={accentColor}
                  editMode={editMode}
                  onLongPressStart={startLongPress}
                  onLongPressEnd={cancelLongPress}
                  justDraggedRef={justDraggedRef}
                />
              ) : (
                <SortablePhotoThumb
                  key={p.id}
                  photo={p}
                  index={idx}
                  photos={photos.filter(x => !isPdfRecord(x) && !x.uploading)}
                  onDelete={onDelete}
                  onCaptionSave={onCaptionSave}
                  disabled={uploading}
                  accentColor={accentColor}
                  onLightboxChange={setLightboxOpen}
                  editMode={editMode}
                  onLongPressStart={startLongPress}
                  onLongPressEnd={cancelLongPress}
                  photoGroups={photoGroups}
                  onGroupAssign={onGroupAssign}
                  justDraggedRef={justDraggedRef}
                />
              );
            });
          })()}
          {/* Кнопка «Добавить» — последняя ячейка той же сетки, чтобы вставать
              на место следующего фото, а не на новую строку */}
          {canAdd && showAdd && (
            <UploadSlot
              onFiles={files => onUpload(files)}
              uploading={uploading}
              multiple={true}
              accentColor={accentColor}
              acceptPdf={acceptPdf}
            />
          )}
        </div>
      </SortableContext>

      <DragOverlay>
        <PhotoDragOverlayGhost photo={activePhoto} accentColor={accentColor} />
      </DragOverlay>
    </DndContext>
  );
}

// ─── Одна секция группового грида: заголовок + своя SortableContext ─────────
// Заголовок — сам droppable-таргет с id секции: у пустой группы нет ни одной
// карточки, чтобы навести на неё курсор при drag, а секция как область — есть.
function PhotoGroupSection({ containerId, groupId, title, photoIds, photos, onDelete, onCaptionSave, onUpload, uploading, accentColor, twoCol, editMode, onLongPressStart, onLongPressEnd, canAdd, showAdd, acceptPdf, photoGroups, onGroupAssign, justDraggedRef, indexById, onDone, onRenameGroup, onDeleteGroup }) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: containerId });
  // Поле имени — неконтролируемое (defaultValue), поэтому очистка кнопкой
  // «×» идёт напрямую через DOM-ref, а не через React-state: перерисовка с
  // тем же defaultValue не тронула бы уже введённый DOM-текст.
  const nameInputRef = useRef(null);
  const sectionPhotos = photoIds.map(id => photos.find(p => p.id === id)).filter(Boolean);
  // Пустая именная группа не должна занимать высоту целого грида — только
  // «Без группы» всегда рисует полный грид (там живёт UploadSlot).
  const isEmpty = sectionPhotos.length === 0 && containerId !== 'group:none';

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {/* Имя Группы редактируется прямо тут — не только сразу после
            создания, а в ЛЮБОЙ момент, когда эта секция активна (Наборы
            убрали, это теперь единственный способ назвать/переназвать
            Группу помимо шторки в Lightbox). «Без группы» — не Группа,
            переименовать нельзя. Найдено на DEV 2026-09-11. */}
        {editMode && groupId != null ? (
          <>
            <input
              ref={nameInputRef}
              key={groupId}
              defaultValue={title}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== title) onRenameGroup?.(groupId, v);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              data-ph-no-capture
              style={{
                fontSize: 11, fontWeight: 700, color: 'var(--text3)', background: 'none',
                border: 'none', borderBottom: '1px dashed var(--border2)', outline: 'none',
                padding: '0 0 1px', minWidth: 30, maxWidth: 200,
              }}
            />
            {/* «×» очищает само ПОЛЕ ИМЕНИ (чтобы напечатать название с нуля,
                без выделения старого текста) — саму Группу не трогает.
                Удаление Группы — отдельная кнопка с иконкой корзины ниже,
                той же, что везде в проекте (Icon name="trash"). Найдено на
                DEV 2026-09-11 (Павел: «×» путали с удалением группы). */}
            <button
              type="button"
              onClick={() => {
                if (nameInputRef.current) {
                  nameInputRef.current.value = '';
                  nameInputRef.current.focus();
                }
              }}
              data-ph-capture="photo_group_clear_name"
              title={t('photoGroups.clearName')}
              style={{
                flexShrink: 0, width: 16, height: 16, borderRadius: '50%',
                border: 'none', background: 'var(--border2)', color: 'var(--text3)',
                fontSize: 11, lineHeight: '16px', textAlign: 'center', padding: 0,
                cursor: 'pointer',
              }}
            >×</button>
            <button
              type="button"
              onClick={() => onDeleteGroup?.(groupId)}
              data-ph-capture="photo_group_delete"
              title={t('photoGroups.deleteGroup')}
              style={{
                flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 18, height: 18, borderRadius: 4, border: 'none', background: 'none',
                color: 'var(--text3)', padding: 0, cursor: 'pointer',
              }}
            ><Icon name="trash" size={13} /></button>
          </>
        ) : (
          <span>{title}</span>
        )}
        <span style={{ fontWeight: 500, color: 'var(--text3)', opacity: 0.7 }}>{sectionPhotos.length}</span>
        {/* "Gotovo" - na urovne IMENNO etoy sekcii: editMode zdes' znachit
            ne "redaktiruetsya vsyo", a "redaktiruetsya IMENNO eta gruppa"
            (sm. activeSectionId v GroupedPhotoGrid) - tryaska i krestiki
            udaleniya po toy zhe prichine vidny tolko tut, a ne po vsemu
            Do/Posle. Naideno na DEV 2026-09-10. */}
        {editMode && (
          <button
            type="button"
            onClick={onDone}
            style={{
              marginLeft: 'auto', padding: '2px 10px', borderRadius: 12,
              background: 'var(--accent)', color: 'white', flexShrink: 0,
              border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >{t('photos.done') || 'Gotovo'}</button>
        )}
      </div>

      {isEmpty ? (
        // Узкая дроп-зона в одну строку вместо полного грида — тот же
        // useDroppable на containerId, drag из другой группы сюда работает
        // 1:1 (dnd-kit сравнивает over.id с id секции, SortableContext для
        // этого не нужен — сортировать в пустой группе всё равно нечего).
        <div
          ref={setNodeRef}
          style={{
            minHeight: 32, borderRadius: 8, padding: '6px 10px',
            border: `1.5px dashed ${isOver ? 'var(--accent, #6366f1)' : 'var(--border2)'}`,
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            fontSize: 11, color: 'var(--text3)', transition: 'border-color 0.15s',
          }}
        >
          {/* minWidth:0 — без него текстовый span как flex-item не сжимается
              (min-width:auto по умолчанию = ширина контента) и вместе с
              flexShrink:0 у кнопки «Добавить» выталкивает её за правый край
              бокса на узких экранах (Android/iOS). flexWrap на контейнере —
              на случай, если даже после сжатия текста места всё равно не
              хватает: кнопка уходит на новую строку внутри бокса, а не
              наружу. Найдено на DEV 2026-09-11. */}
          <span style={{ minWidth: 0 }}>{t('photoGroups.dropHint')}</span>
          {canAdd && showAdd && (
            <UploadSlot
              onFiles={files => onUpload(files, groupId)}
              uploading={uploading} multiple={true} accentColor={accentColor} acceptPdf={acceptPdf}
              compact
            />
          )}
        </div>
      ) : (
        <div
          ref={setNodeRef}
          className={`photo-grid${twoCol ? ' photo-grid--before-after' : ''}`}
          style={{
            display: 'grid', gap: 8, minHeight: 44, borderRadius: 8,
            outline: isOver ? '2px dashed var(--accent, #6366f1)' : 'none',
            outlineOffset: 2, transition: 'outline-color 0.15s',
          }}
        >
          <SortableContext items={photoIds} strategy={rectSortingStrategy}>
            {sectionPhotos.map((p) => {
              if (p.uploading) {
                return <div key={p.id}><UploadingThumb photo={p} accentColor={accentColor} /></div>;
              }
              // Skvoznoy nomer cherez VSE gruppy (a ne "s 1" v kazhdoy) -
              // istochnik istiny - indexById, poschitanny odin raz v
              // GroupedPhotoGrid po poryadku sektsiy. Lokalny map-indeks
              // zdes bolshe ne godится.
              const idx = indexById.get(p.id) ?? 0;
              return isPdfRecord(p) ? (
                <SortablePdfThumb
                  key={p.id} photo={p} index={idx} onDelete={onDelete} disabled={uploading}
                  accentColor={accentColor} editMode={editMode}
                  onLongPressStart={onLongPressStart} onLongPressEnd={onLongPressEnd}
                  justDraggedRef={justDraggedRef}
                />
              ) : (
                <SortablePhotoThumb
                  key={p.id} photo={p} index={idx}
                  photos={sectionPhotos.filter(x => !isPdfRecord(x) && !x.uploading)}
                  onDelete={onDelete} onCaptionSave={onCaptionSave} disabled={uploading}
                  accentColor={accentColor} onLightboxChange={() => {}}
                  editMode={editMode} onLongPressStart={onLongPressStart} onLongPressEnd={onLongPressEnd}
                  photoGroups={photoGroups} onGroupAssign={onGroupAssign}
                  justDraggedRef={justDraggedRef}
                />
              );
            })}
            {/* «Добавить» — в конце КАЖДОЙ группы (не только «Без группы»): юзер
                может закинуть фото прямо в нужную группу, без промежуточного
                перетаскивания из «Без группы». groupId=null для «Без группы». */}
            {canAdd && showAdd && (
              <UploadSlot
                onFiles={files => onUpload(files, groupId)}
                uploading={uploading} multiple={true} accentColor={accentColor} acceptPdf={acceptPdf}
              />
            )}
          </SortableContext>
        </div>
      )}
    </div>
  );
}

// ─── Групповой рендер: секции по Группам Объекта + «Без группы», drag МЕЖДУ
// секциями (dnd-kit multi-container: onDragOver переставляет контейнер
// оптимистично, onDragEnd сохраняет и группу, и порядок). Рисуется, только
// когда на Объекте настроена хотя бы одна Группа — до тех пор ReportPhotoGallery
// рисует FlatPhotoGrid без единого изменения.
function GroupedPhotoGrid({ photos, setPhotos, groups, onUpload, onDelete, onCaptionSave, uploading, onReorder, onGroupAssign, acceptPdf, twoCol, editMode, onEditModeChange, showAdd, canAdd, accentColor, photoGroups, pendingGroupId, onPendingGroupIdConsumed }) {
  const { t, i18n } = useTranslation();
  const [activePhoto, setActivePhoto] = useState(null);
  const longPressTimer = useRef(null);
  // Kakaya SEKTSIYA seychas redaktiruetsya - ne "ves blok". editMode (prop
  // sverhu, iz PhotosSection) ostaetsya obshchim pereklyuchatelem "idet
  // redaktirovanie gde-to zdes" (nuzhen ReportPhotoGallery dlya
  // GroupToolsBar i roditelyu dlya exitAllEditModes/akkordeona), a
  // activeSectionId suzhaet tryasku/krestiki/Gotovo do odnoy sektsii.
  // Vneshniy sbros editMode->false (klik vne bloka) dolzhen pogasit i
  // lokalnuyu sektsiyu - sm. useEffect nizhe.
  const [activeSectionId, setActiveSectionId] = useState(null);
  useEffect(() => {
    if (!editMode) setActiveSectionId(null);
  }, [editMode]);
  // Только что созданную кнопкой «Добавить группу» Группу сразу активируем —
  // юзер видит поле имени и может сразу его напечатать, без второго
  // жми-держи по фото. Сигнал одноразовый: consumed сбрасывает его в
  // ReportPhotoGallery, иначе эффект бы срабатывал повторно на каждый
  // ре-рендер с тем же id.
  useEffect(() => {
    if (pendingGroupId == null) return;
    setActiveSectionId(`group:${pendingGroupId}`);
    onPendingGroupIdConsumed?.();
  }, [pendingGroupId, onPendingGroupIdConsumed]);

  const handleRenameGroup = async (groupId, label) => {
    try { await photoGroups?.renameGroup?.(groupId, label); }
    catch (e) { toast.error(e.message); }
  };

  // Удаляем саму Группу — если в ней были фото, они «возвращаются в Без
  // группы» не отдельным запросом, а сами по себе: архивная группа пропадает
  // из photoGroups.groups, containerOf на следующем рендере больше не находит
  // её среди groups и кладёт эти фото в group:none. Сброс activeSectionId —
  // на случай, если удаляли именно ту секцию, что была открыта.
  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm(t('photoGroups.groupDeleteConfirm'))) return;
    try {
      await photoGroups?.archiveGroup?.(groupId);
      setActiveSectionId(prev => (prev === `group:${groupId}` ? null : prev));
    } catch (e) { toast.error(e.message); }
  };

  const startLongPress = (containerId) => {
    // Гейт по activeSectionId, а НЕ по внешнему editMode: editMode может
    // остаться true «с прошлой жизни» этого блока — например, юзер жал-
    // держал во ФЛОСКОМ режиме (групп ещё не было), потом применил Набор
    // прямо посреди этого же нажатия — FlatPhotoGrid размонтировался,
    // GroupedPhotoGrid смонтировался заново с activeSectionId=null, а
    // editMode (проп сверху) остался true. Гейт по editMode тут навечно
    // блокировал бы новый лонг-пресс (условие всегда true, activeSectionId
    // некому больше выставить) — фото не тряслись и не перетаскивались, и
    // ни одной кнопки «Готово» на экране, чтобы выйти. activeSectionId —
    // точный сигнал «какая-то секция уже активна», не зависящий от того,
    // как мы сюда попали. Баг найден на DEV 2026-09-10.
    if (activeSectionId) return;
    longPressTimer.current = setTimeout(() => {
      setActiveSectionId(containerId);
      onEditModeChange?.(true);
      if (navigator.vibrate) navigator.vibrate(40);
    }, 400);
  };
  const cancelLongPress = () => { if (longPressTimer.current) clearTimeout(longPressTimer.current); };
  const finishSectionEdit = () => {
    setActiveSectionId(null);
    onEditModeChange?.(false);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: editMode ? 5 : 99999 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: editMode ? 0 : 250, tolerance: editMode ? 5 : 8 } })
  );

  const sections = [
    ...groups.map(g => ({ id: `group:${g.id}`, groupId: g.id, title: photoGroupLabel(g, i18n.language) || '—' })),
    { id: 'group:none', groupId: null, title: t('photoGroups.ungroupedSection') },
  ];
  const containerOf = (p) => (p.photo_group_id && groups.some(g => g.id === p.photo_group_id)) ? `group:${p.photo_group_id}` : 'group:none';
  const sectionPhotoIds = (containerId) => photos.filter(p => containerOf(p) === containerId).map(p => p.id);

  // Skvoznaya numeratsiya kartochek cherez VSE sektsii (a ne "s 1" v
  // kazhdoy) - schitaem odin raz po poryadku sektsiy, a ne po poryadku
  // samogo photos (tam elementy raznyh sektsiy peremeshany posle
  // reordera, sm. handleDragEnd).
  const indexById = new Map();
  let globalIdx = 0;
  sections.forEach(s => {
    sectionPhotoIds(s.id).forEach(id => {
      const p = photos.find(x => x.id === id);
      if (p && !p.uploading) indexById.set(id, globalIdx++);
    });
  });

  // Контейнер элемента: сам photo.id → его секция; id секции (навели на пустую
  // область/заголовок) → сама секция.
  const resolveContainer = (id) => {
    if (sections.some(s => s.id === id)) return id;
    const p = photos.find(x => x.id === id);
    return p ? containerOf(p) : null;
  };

  // Группа фото НА МОМЕНТ начала перетаскивания — handleDragOver может
  // оптимистично поменять photo_group_id в стейте ещё ДО отпускания, поэтому
  // сверять «изменилась ли группа» с текущим photos в handleDragEnd нельзя:
  // к этому моменту там уже новое значение. Источник правды — этот ref.
  const dragStartGroupId = useRef(null);
  // Аналогичный ref есть в FlatPhotoGrid — гасит synthetic click после драга;
  // здесь тот же приём, но цепочка пропсов длиннее (через PhotoGroupSection).
  const justDraggedRef = useRef(false);

  const handleDragStart = ({ active }) => {
    const p = photos.find(x => x.id === active.id) || null;
    setActivePhoto(p);
    dragStartGroupId.current = p ? (containerOf(p) === 'group:none' ? null : p.photo_group_id) : null;
    justDraggedRef.current = true;
  };

  // Перетащили в другую секцию — оптимистично переносим photo_group_id
  // локально, чтобы карточка визуально встала в новую секцию ДО отпускания.
  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    const fromContainer = resolveContainer(active.id);
    const toContainer = resolveContainer(over.id);
    if (!toContainer || fromContainer === toContainer) return;
    const newGroupId = sections.find(s => s.id === toContainer)?.groupId ?? null;
    setPhotos(prev => prev.map(p => (p.id === active.id ? { ...p, photo_group_id: newGroupId } : p)));
  };

  const handleDragEnd = ({ active, over }) => {
    setActivePhoto(null);
    if (!over) return;
    const toContainer = resolveContainer(over.id);
    if (!toContainer) return;
    const newGroupId = sections.find(s => s.id === toContainer)?.groupId ?? null;
    const movedPhoto = photos.find(p => p.id === active.id);
    const groupChanged = dragStartGroupId.current !== newGroupId;

    // Финальный порядок фото внутри секции-приёмника. Обычный случай — active
    // уже входит в destIds (свой контейнер не менялся, либо onDragOver уже
    // оптимистично проставил photo_group_id) — считаем ТОЧНО как dnd-kit'овский
    // arrayMove (см. FlatPhotoGrid.handleDragEnd). Раньше тут была своя вставка
    // «удалить active, вставить перед over» БЕЗ учёта направления движения:
    // при переносе ВПЕРЁД (например ДО10 → на место идущего сразу ЗА ним ДО2)
    // active удалялся, индекс over в укороченном массиве совпадал с исходной
    // позицией active, и карточка вставлялась туда же, откуда её взяли —
    // визуально ничего не менялось; сдвинуть её получалось только бросив на
    // СЛЕДУЮЩИЙ за целью элемент. Найдено на DEV 2026-09-10 (мобилка,
    // «Без группы»).
    const destIds = sectionPhotoIds(toContainer);
    const overIsPhoto = photos.some(p => p.id === over.id);
    const oldIndex = destIds.indexOf(active.id);
    let orderedDestIds;
    if (oldIndex >= 0) {
      const newIndex = overIsPhoto ? destIds.indexOf(over.id) : destIds.length - 1;
      orderedDestIds = arrayMove(destIds, oldIndex, newIndex);
    } else {
      // Настоящий межгрупповой прыжок без промежуточного onDragOver над этим
      // контейнером — active тут раньше не появлялся, вставляем на место
      // over (или в конец, если бросили на пустую область секции).
      orderedDestIds = [...destIds];
      const insertAt = overIsPhoto ? orderedDestIds.indexOf(over.id) : orderedDestIds.length;
      orderedDestIds.splice(insertAt >= 0 ? insertAt : orderedDestIds.length, 0, active.id);
    }

    // Пересобираем сам ПОРЯДОК ЭЛЕМЕНТОВ массива photos, а не только
    // photo_group_id — секции рендерятся через photos.filter(...).map(...)
    // (PhotoGroupSection/sectionPhotoIds), то есть видимый порядок внутри
    // секции — это порядок элементов В МАССИВЕ, отфильтрованных по секции.
    // Раньше здесь менялось только поле photo_group_id (через .map без смены
    // позиций) — после отпускания карточка «отскакивала» на старое место:
    // тот же баг что при чистом реордере внутри одной секции без смены
    // группы (там photo_group_id вообще не менялся, порядок — тем более).
    // Глобальное перемешивание элементов ДРУГИХ секций внутри массива не
    // важно — каждая секция фильтрует свою подвыборку независимо, важен
    // только относительный порядок внутри секции-приёмника.
    setPhotos(prev => {
      const destSet = new Set(orderedDestIds);
      const otherItems = prev.filter(p => p.id !== active.id && !destSet.has(p.id));
      const destById = new Map(prev.filter(p => destSet.has(p.id)).map(p => [p.id, p]));
      const newDestItems = orderedDestIds.map(id => (
        id === active.id ? { ...movedPhoto, photo_group_id: newGroupId } : destById.get(id)
      ));
      return [...otherItems, ...newDestItems];
    });

    if (groupChanged) onGroupAssign(active.id, newGroupId);
    const order = orderedDestIds.map((id, i) => ({ id, display_order: i }));
    onReorder(order);
    Analytics.photoReordered(movedPhoto?.photo_type, order.length);
    // Перетащили в ДРУГУЮ группу — сразу выходим из режима редактирования
    // (как по кнопке «Готово»): перенос — это одно завершённое действие,
    // не нужно тут же тыкать «Готово» руками. Обычный реордер ВНУТРИ той же
    // группы — не считается, editMode остаётся (юзер может продолжать
    // двигать фото в этой же секции).
    if (groupChanged) finishSectionEdit();
    // Сброс с задержкой 0 — см. комментарий у объявления justDraggedRef выше.
    setTimeout(() => { justDraggedRef.current = false; }, 0);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {sections.map((s) => (
        <PhotoGroupSection
          key={s.id}
          containerId={s.id}
          groupId={s.groupId}
          title={s.title}
          photoIds={sectionPhotoIds(s.id)}
          photos={photos}
          onDelete={onDelete}
          onCaptionSave={onCaptionSave}
          onUpload={onUpload}
          uploading={uploading}
          accentColor={accentColor}
          twoCol={twoCol}
          editMode={activeSectionId === s.id}
          onLongPressStart={() => startLongPress(s.id)}
          onLongPressEnd={cancelLongPress}
          canAdd={canAdd}
          showAdd={showAdd}
          acceptPdf={acceptPdf}
          photoGroups={photoGroups}
          onGroupAssign={onGroupAssign}
          justDraggedRef={justDraggedRef}
          indexById={indexById}
          onDone={finishSectionEdit}
          onRenameGroup={handleRenameGroup}
          onDeleteGroup={handleDeleteGroup}
        />
      ))}
      <DragOverlay>
        <PhotoDragOverlayGhost photo={activePhoto} accentColor={accentColor} />
      </DragOverlay>
    </DndContext>
  );
}

// ─── Тулбар «Добавить группу» — единственный способ завести на Объекте
// первую (и любую следующую) Группу прямо из грида фото, без модалки и без
// ухода в Настройки. «Наборы» из этого флоу убраны по просьбе Павла
// (2026-09-11) — материализация целого Набора больше не нужна: пользователю
// проще завести пустую группу и сразу переименовать её инлайн (см.
// PhotoGroupSection). Видна только в editMode и только тем, у кого
// canEditGroups.
function GroupToolsBar({ photoGroups, onActivated }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  if (!photoGroups || !photoGroups.canEditGroups) return null;

  const handleAdd = async () => {
    setBusy(true);
    try {
      const n = (photoGroups.groups?.length || 0) + 1;
      const created = await photoGroups.createGroup(t('photoGroups.defaultName', { n }));
      if (created) onActivated?.(created.id);
    }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const toolBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 12px', borderRadius: 14, border: '1.5px dashed var(--accent, #6366f1)',
    background: 'none', color: 'var(--accent, #6366f1)', fontSize: 12, fontWeight: 700, cursor: 'pointer',
    opacity: busy ? 0.6 : 1,
  };

  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
      <button type="button" onClick={handleAdd} disabled={busy}
        data-ph-capture="photo_group_add" style={toolBtn}>{t('photoGroups.addGroupBtn')}</button>
    </div>
  );
}

export function ReportPhotoGallery({ photos, setPhotos, label, accentColor, onUpload, onDelete, onCaptionSave, uploading, onReorder, acceptPdf = false, max = MAX_BEFORE_AFTER, twoCol = false, editMode = false, onEditModeChange, showAdd = true, photoGroups, onGroupAssign, visibleGroupIds, onGroupsActivated }) {
  const { t } = useTranslation();
  const canAdd = photos.length < max;
  // Только что созданная кнопкой «Добавить группу» группа — секция должна
  // сразу открыться в редактировании (см. GroupedPhotoGrid), чтобы юзер мог
  // тут же переименовать её инлайн, без второго клика.
  const [pendingGroupId, setPendingGroupId] = useState(null);
  // Секции — только группы, видимые в ЭТОЙ Работе (см. visibleGroupIds в
  // PhotosSection), а не весь photoGroups.groups Объекта целиком: у соседней
  // Работы того же Объекта набор групп может быть другим.
  const visibleGroups = ((photoGroups && photoGroups.groups) || []).filter(g => visibleGroupIds && visibleGroupIds.has(g.id));
  const hasGroups = visibleGroups.length > 0;

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* Label bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        marginBottom: 8,
      }}>
        {label && (<>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, flexShrink: 0, display: 'inline-block' }} />
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.5px',
            textTransform: 'uppercase', color: accentColor,
          }}>{label}</span>
        </>)}
        {(label || editMode) && (
          <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
            {editMode && !hasGroups ? (
              <button
                onClick={() => onEditModeChange?.(false)}
                style={{
                  padding: '2px 10px', borderRadius: 12,
                  background: 'var(--accent)', color: 'white',
                  border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                }}
              >{t('photos.done') || 'Готово'}</button>
            ) : (
              <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500 }}>
                {photos.length}/{max}
              </span>
            )}
          </div>
        )}
      </div>

      {editMode && (
        <GroupToolsBar
          photoGroups={photoGroups}
          onActivated={(id) => { onGroupsActivated?.(id); setPendingGroupId(id); }}
        />
      )}

      {/* Gallery grid with DnD — плоский грид, пока на Объекте нет ни одной
          Группы (подавляющее большинство Работ); групповой — как только
          появилась хотя бы одна. */}
      {hasGroups ? (
        <GroupedPhotoGrid
          photos={photos} setPhotos={setPhotos} groups={visibleGroups}
          onUpload={onUpload} onDelete={onDelete} onCaptionSave={onCaptionSave}
          uploading={uploading} onReorder={onReorder} onGroupAssign={onGroupAssign}
          acceptPdf={acceptPdf} twoCol={twoCol} editMode={editMode} onEditModeChange={onEditModeChange}
          showAdd={showAdd} canAdd={canAdd} accentColor={accentColor} photoGroups={photoGroups}
          pendingGroupId={pendingGroupId} onPendingGroupIdConsumed={() => setPendingGroupId(null)}
        />
      ) : (
        <FlatPhotoGrid
          photos={photos} setPhotos={setPhotos} onUpload={onUpload} onDelete={onDelete}
          onCaptionSave={onCaptionSave} uploading={uploading} onReorder={onReorder}
          acceptPdf={acceptPdf} twoCol={twoCol} editMode={editMode} onEditModeChange={onEditModeChange}
          showAdd={showAdd} canAdd={canAdd} accentColor={accentColor}
          photoGroups={photoGroups} onGroupAssign={onGroupAssign}
        />
      )}
    </div>
  );
}

// ─── Main PhotosSection ──────────────────────────────────────────────────────
/**
 * initialGeneralCount / initialReportCount — счётчики из списка Работ
 * (properties.js: photo_general_count / photo_report_count). Заголовки
 * аккордеонов рисуются с ними сразу, пока идёт GET /tasks/:id/photos: раньше
 * весь блок был спрятан за плашкой «Загрузка...», из-за чего при раскрытии
 * карточки на PROD «Служебная информация» и «Фото до и после» появлялись
 * рывком через сотни миллисекунд. Спиннер остался, но только внутри
 * раскрытого аккордеона — там он честно ждёт сами фото.
 */
export default function PhotosSection({
  taskId,
  propertyId,
  demoBeforeAfterTarget = false,
  hideBeforeAfter = false,
  initialGeneralCount = 0,
  initialVideoCount = 0,
  initialReportCount = 0,
}) {
  const { t } = useTranslation();
  const [openGeneral, setOpenGeneral] = useState(false);
  // ⚠️ Стартуем со значения из списка Работ (properties.js: video_count).
  // Своего запроса тут нет и быть не должно: VideosSection монтируется только
  // при раскрытом аккордеоне, а точка и иконка нужны как раз в свёрнутом.
  // Когда аккордеон раскроют, VideosSection уточнит число через onCountChange.
  const [videoCount, setVideoCount] = useState(initialVideoCount);
  const [openReport, setOpenReport] = useState(false);
  const [generalEditMode, setGeneralEditMode] = useState(false);
  const [beforeEditMode, setBeforeEditMode] = useState(false);
  const [afterEditMode, setAfterEditMode] = useState(false);

  const [generalPhotos, setGeneralPhotos] = useState([]);
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [uploadingGeneral, setUploadingGeneral] = useState(false);
  const [uploadingBefore, setUploadingBefore] = useState(false);
  const [uploadingAfter, setUploadingAfter] = useState(false);
  // Защита от двойного вызова onFiles на iOS (gallery + camera input оба могут сработать)
  const uploadGuard = useRef({});

  const BASE = process.env.REACT_APP_API_URL || '/api';

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('pm_token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }, []);

  // Группы фото «До/После» — на Объекте, общие для обеих колонок и для всех
  // Работ этого Объекта. Выключены на инспекциях (hideBeforeAfter): там блока
  // До/После вообще нет.
  const photoGroupsApi = usePhotoGroups({ propertyId, enabled: !hideBeforeAfter && !!propertyId });

  // Дефолт — БЕЗ групп для каждой Работы отдельно, даже если у Объекта они уже
  // есть (материализованы другой Работой того же Объекта). Видимый в ЭТОЙ
  // Работе набор групп — не весь photoGroupsApi.groups, а только те, что
  // реально относятся к НЕЙ: (а) на её фото уже стоит эта группа, либо
  // (б) юзер сам активировал её тут — «+ группа»/«Применить набор». У другой
  // Работы того же Объекта видимый набор групп может быть совсем другим — см.
  // visibleGroupIds ниже. Найдено на DEV 2026-09-10.
  const [activatedGroupIds, setActivatedGroupIds] = useState(() => new Set());
  const activateGroupIds = (ids) => {
    const list = Array.isArray(ids) ? ids : [ids];
    if (!list.length) return;
    setActivatedGroupIds(prev => {
      const next = new Set(prev);
      list.forEach(id => next.add(id));
      return next;
    });
  };

  useEffect(() => {
    fetchPhotos();
  }, [taskId]);

  const fetchPhotos = async () => {
    setLoadFailed(false);
    try {
      const res = await fetch(`${BASE}/tasks/${taskId}/photos`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Ошибка загрузки фото');
      const data = await res.json();
      setGeneralPhotos(data.filter(p => p.photo_type === 'general' || !p.photo_type));
      setBeforePhotos(data.filter(p => p.photo_type === 'before'));
      setAfterPhotos(data.filter(p => p.photo_type === 'after'));
      setLoaded(true);
    } catch (e) {
      // Раньше ошибка оставляла блок в вечной «Загрузке» без выхода: loaded так
      // и не выставлялся. Теперь заголовки на месте, а в теле — кнопка «Ещё раз».
      setLoadFailed(true);
      toast.error(e.message);
    }
  };

  // Generic upload function — поддерживает image/* и PDF
  const uploadPhoto = useCallback(async (file, photoType, maxWidth, quality, groupId) => {
    const fileIsPdf = file.type === 'application/pdf';
    const sizeBefore = (file.size / 1024).toFixed(0);

    // Отдаём управление React перед тяжёлой операцией — UI успевает перерисоваться
    await new Promise(r => setTimeout(r, 30));

    const uploadBlob = fileIsPdf ? file : await compressImage(file, maxWidth, quality);
    const contentType = fileIsPdf ? 'application/pdf' : 'image/jpeg';
    const sizeAfter = (uploadBlob.size / 1024).toFixed(0);

    const presignRes = await fetch(`${BASE}/tasks/${taskId}/photos/presign`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ contentType, filename: file.name, photo_type: photoType }),
    });
    if (!presignRes.ok) { const err = await presignRes.json(); throw new Error(err.error || 'Ошибка presign'); }
    const { uploadUrl, key } = await presignRes.json();

    // XHR используется вместо fetch для совместимости с iOS Safari (PUT + Blob)
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Ошибка загрузки в хранилище (${xhr.status})`));
      xhr.onerror = () => reject(new Error('Ошибка загрузки в хранилище'));
      xhr.send(uploadBlob);
    });

    const confirmRes = await fetch(`${BASE}/tasks/${taskId}/photos`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ key, filename: file.name, size: uploadBlob.size, photo_type: photoType, photo_group_id: groupId ?? null }),
    });
    if (!confirmRes.ok) throw new Error('Ошибка сохранения записи');
    const saved = await confirmRes.json();

    toast.success(fileIsPdf
      ? `PDF загружен (${sizeAfter} KB)`
      : `Фото загружено (${sizeBefore}→${sizeAfter} KB)`
    );
    return saved;
  }, [BASE, taskId, getHeaders, t]);

  // Optimistic upload: сразу добавляем плейсхолдер в стейт, загружаем на фоне,
  // потом заменяем плейсхолдер реальным фото. Только на Android — на iOS задержки нет.
  const uploadWithPlaceholder = useCallback(async (files, setter, photoType, maxWidth, quality, max, groupId) => {
    let currentLen = 0;
    setter(prev => { currentLen = prev.filter(p => !p.uploading).length; return prev; });
    const remaining = max - currentLen;
    if (remaining <= 0) { toast.error(`Максимум ${max} фото`); return; }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) toast(`Загружаем первые ${remaining}`, { icon: 'ℹ️' });

    for (const file of toUpload) {
      // Создаём временный объект-плейсхолдер
      const tempId = `temp_${Date.now()}_${Math.random()}`;
      const localUrl = (isAndroid && file.type.startsWith('image/')) ? URL.createObjectURL(file) : null;
      const placeholder = { id: tempId, url: localUrl, uploading: true, photo_type: photoType, photo_group_id: groupId ?? null };

      // Мгновенно показываем плейсхолдер — flushSync гарантирует рендер до сжатия
      flushSync(() => setter(prev => [...prev, placeholder]));

      try {
        const saved = await uploadPhoto(file, photoType, maxWidth, quality, groupId);
        // Убираем плейсхолдер и добавляем реальное фото
        setter(prev => {
          const filtered = prev.filter(p => p.id !== tempId);
          return [...filtered, saved];
        });
        if (localUrl) URL.revokeObjectURL(localUrl);
        Analytics.photoUploaded(photoType);
        // Сигнал для демо-режиссуры: пользователь сам загрузил фото
        window.dispatchEvent(new CustomEvent('photo-uploaded', { detail: { photoType } }));
      } catch (e) {
        setter(prev => prev.filter(p => p.id !== tempId));
        if (localUrl) URL.revokeObjectURL(localUrl);
        toast.error(`${file.name}: ${e.message}`);
      }
    }
  }, [uploadPhoto]);

  const guardedUpload = useCallback(async (key, setter, setUploading, photoType, maxWidth, quality, max, files, groupId) => {
    if (uploadGuard.current[key]) return;
    uploadGuard.current[key] = true;
    setUploading(true);
    try {
      await uploadWithPlaceholder(files, setter, photoType, maxWidth, quality, max, groupId);
    } finally {
      setUploading(false);
      setTimeout(() => { uploadGuard.current[key] = false; }, 1000);
    }
  }, [uploadWithPlaceholder]);

  const handleGeneralFiles = useCallback(async (files, groupId) => {
    setOpenGeneral(true);
    await guardedUpload('general', setGeneralPhotos, setUploadingGeneral, 'general', GENERAL_MAX_WIDTH, GENERAL_QUALITY, MAX_GENERAL, files, groupId);
  }, [guardedUpload]);

  const handleBeforeFiles = useCallback(async (files, groupId) => {
    setOpenReport(true);
    await guardedUpload('before', setBeforePhotos, setUploadingBefore, 'before', REPORT_MAX_WIDTH, REPORT_QUALITY, MAX_BEFORE_AFTER, files, groupId);
  }, [guardedUpload]);

  const handleAfterFiles = useCallback(async (files, groupId) => {
    setOpenReport(true);
    await guardedUpload('after', setAfterPhotos, setUploadingAfter, 'after', REPORT_MAX_WIDTH, REPORT_QUALITY, MAX_BEFORE_AFTER, files, groupId);
  }, [guardedUpload]);

  const handleDelete = async (photoId) => {
    try {
      const res = await fetch(`${BASE}/tasks/${taskId}/photos/${photoId}`, {
        method: 'DELETE', headers: getHeaders(),
      });
      // 404 = фото уже удалено с другого устройства — убираем локально,
      // а не оставляем «неудаляемое» фото до перезагрузки страницы.
      const stale = res.status === 404;
      if (!res.ok && !stale) throw new Error(t('photos.deleteError'));
      if (stale) track('task_photo_delete_stale', { task_id: taskId });
      const deletedRecord =
        generalPhotos.find(p => p.id === photoId) ||
        beforePhotos.find(p => p.id === photoId) ||
        afterPhotos.find(p => p.id === photoId);
      const deletedType = deletedRecord?.photo_type || 'unknown';
      Analytics.photoDeleted(deletedType);
      setGeneralPhotos(prev => prev.filter(p => p.id !== photoId));
      setBeforePhotos(prev => prev.filter(p => p.id !== photoId));
      setAfterPhotos(prev => prev.filter(p => p.id !== photoId));
      if (stale) toast(t('photos.alreadyDeleted'));
      else toast.success(isPdfRecord(deletedRecord) ? t('photos.documentDeleted') : t('photos.deleted'));
    } catch (e) { toast.error(e.message); throw e; }
  };

  const handleCaptionSave = async (photoId, captionText) => {
    try {
      const res = await fetch(`${BASE}/tasks/${taskId}/photos/${photoId}/caption`, {
        method: 'PATCH', headers: getHeaders(),
        body: JSON.stringify({ caption: captionText }),
      });
      if (!res.ok) throw new Error('Ошибка сохранения подписи');
      const { caption } = await res.json();
      // Update caption in local state across all galleries
      const updater = prev => prev.map(p => p.id === photoId ? { ...p, caption: caption || '' } : p);
      setGeneralPhotos(updater);
      setBeforePhotos(updater);
      setAfterPhotos(updater);
    } catch (e) { toast.error(e.message); }
  };

  const handleGroupAssign = async (photoId, groupId) => {
    try {
      const res = await fetch(`${BASE}/tasks/${taskId}/photos/${photoId}`, {
        method: 'PATCH', headers: getHeaders(),
        body: JSON.stringify({ photo_group_id: groupId }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Ошибка сохранения группы');
      }
      const updated = await res.json();
      const newGroupId = updated.photo_group_id ?? null;

      // Назначение группы из шторки в Lightbox раньше только меняло
      // photo_group_id НА МЕСТЕ (.map без смены позиции в массиве) — итоговая
      // позиция внутри НОВОЙ группы зависела от старого display_order этого
      // фото (унаследованного от прежней группы/«Без группы»), почти
      // случайно, а не «в конец», как при обычном перетаскивании между
      // секциями (см. handleDragEnd в GroupedPhotoGrid). Явно пересобираем
      // порядок внутри группы-приёмника: все её текущие фото, затем это —
      // последним, и шлём тот же /reorder, что и драг. Найдено на DEV
      // 2026-09-11.
      for (const [list, setList] of [[beforePhotos, setBeforePhotos], [afterPhotos, setAfterPhotos]]) {
        if (!list.some(p => p.id === photoId)) continue;
        const destSiblings = list.filter(p => p.id !== photoId && (p.photo_group_id ?? null) === newGroupId);
        const orderedIds = [...destSiblings.map(p => p.id), photoId];
        setList(prev => {
          const updatedPrev = prev.map(p => (p.id === photoId ? { ...p, photo_group_id: newGroupId } : p));
          const destSet = new Set(orderedIds);
          const others = updatedPrev.filter(p => !destSet.has(p.id));
          const destById = new Map(updatedPrev.filter(p => destSet.has(p.id)).map(p => [p.id, p]));
          const newDestItems = orderedIds.map(id => destById.get(id));
          return [...others, ...newDestItems];
        });
        handleReorder(orderedIds.map((id, i) => ({ id, display_order: i })));
        break;
      }
    } catch (e) { toast.error(e.message); }
  };

  const handleReorder = async (order) => {
    try {
      const res = await fetch(`${BASE}/tasks/${taskId}/photos/reorder`, {
        method: 'PATCH', headers: getHeaders(),
        body: JSON.stringify({ order }),
      });
      if (!res.ok) throw new Error('Ошибка сохранения порядка');
    } catch (e) { toast.error(e.message); }
  };

  const visibleGroupIds = (() => {
    const ids = new Set(activatedGroupIds);
    beforePhotos.forEach(p => { if (p.photo_group_id != null) ids.add(p.photo_group_id); });
    afterPhotos.forEach(p => { if (p.photo_group_id != null) ids.add(p.photo_group_id); });
    return ids;
  })();

  // До ответа сервера показываем счётчики из списка Работ — цифра в заголовке
  // та же, что будет после загрузки, поэтому подмены на глазах не происходит.
  const generalCount = loaded ? generalPhotos.length : initialGeneralCount;
  const reportCount = loaded ? beforePhotos.length + afterPhotos.length : initialReportCount;
  const reportTotal = MAX_BEFORE_AFTER * 2;

  // Тело аккордеона: пока фото не приехали — спиннер, ошибка — повтор.
  const accordionBody = (children) => {
    if (loadFailed) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', fontSize: 13, color: 'var(--text3)' }}>
          <span>{t('photos.loadError')}</span>
          <button
            onClick={() => { Analytics.photosReloadClicked(); fetchPhotos(); }}
            data-ph-capture-attribute-action="photos_reload"
            style={{
              padding: '2px 10px', borderRadius: 12, background: 'transparent',
              color: 'var(--accent)', border: '1px solid var(--accent)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >{t('photos.retry')}</button>
        </div>
      );
    }
    if (!loaded) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', color: 'var(--text3)', fontSize: 13 }}>
          <span className="spinner" style={{ width: 16, height: 16 }} /> {t('photos.loading')}
        </div>
      );
    }
    return children;
  };

  const anyEditMode = generalEditMode || beforeEditMode || afterEditMode;
  const exitAllEditModes = () => {
    setGeneralEditMode(false);
    setBeforeEditMode(false);
    setAfterEditMode(false);
  };

  // Accordion row style, mirroring Dashboard DoneSection
  const accordionRow = (isOpen) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
    background: isOpen ? 'rgba(79,142,247,0.12)' : 'var(--bg2)',
    border: '1px solid', borderColor: isOpen ? 'var(--accent)' : 'var(--border)',
    transition: 'background 0.15s, border-color 0.15s',
    userSelect: 'none', marginBottom: 4,
  });

  return (
    <div
      style={{ marginTop: 8 }}
      onClick={e => { if (anyEditMode && e.target === e.currentTarget) exitAllEditModes(); }}
    >
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          onClick={e => { if (anyEditMode && e.target === e.currentTarget) exitAllEditModes(); }}
        >

              {/* ── Accordion: Служебные ── */}
              <div>
                <div style={accordionRow(openGeneral)} onClick={() => !generalEditMode && setOpenGeneral(v => !v)}>
                  {/* Точка загорается, когда внутри что-то есть — фото ИЛИ видео.
                      Раньше она была неизменно серой, и по свёрнутому аккордеону
                      нельзя было понять, пуст он или нет. */}
                  <div style={{
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: (generalCount > 0 || videoCount > 0) ? 'var(--accent)' : 'var(--text3)',
                  }} />
                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', flex: 1 }}>{t('photos.servicePhotos')}</span>
                  {/* Иконка с числом роликов: без неё наличие видео видно только
                      после раскрытия — счётчик рядом считает лишь фото. */}
                  {videoCount > 0 && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      fontSize: 12, color: 'var(--text3)', marginRight: 2,
                    }}>
                      <VideoIcon size={13} />
                      {videoCount}
                    </span>
                  )}
                  {generalEditMode ? (
                    <button
                      onClick={e => { e.stopPropagation(); setGeneralEditMode(false); }}
                      style={{
                        padding: '2px 10px', borderRadius: 12,
                        background: 'var(--accent)', color: 'white',
                        border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      }}
                    >{t('photos.done') || 'Готово'}</button>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>{generalCount}/{MAX_GENERAL}</span>
                  )}
                  <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 6 }}>{openGeneral ? '▲' : '▼'}</span>
                </div>
                {openGeneral && (
                  <div style={{ paddingLeft: 4, paddingBottom: 8 }}>
                    {accordionBody(
                    <ReportPhotoGallery
                      photos={generalPhotos}
                      setPhotos={setGeneralPhotos}
                      label=""
                      accentColor="var(--text3)"
                      onUpload={handleGeneralFiles}
                      onDelete={handleDelete}
                      onCaptionSave={handleCaptionSave}
                      uploading={uploadingGeneral}
                      onReorder={handleReorder}
                      acceptPdf={true}
                      max={MAX_GENERAL}
                      editMode={generalEditMode}
                      onEditModeChange={setGeneralEditMode}
                    />
                    )}
                    {/* Видео живёт здесь же, в служебной информации: работник
                        объясняет голосом то, что фото не передаёт. В отчёт
                        клиенту не идёт — в отличие от «до и после». */}
                    {!generalEditMode && <VideosSection taskId={taskId} onCountChange={setVideoCount} />}
                  </div>
                )}
              </div>

              {/* ── Accordion: До/После ── (скрыт для инспекций — у них нет «после») */}
              {!hideBeforeAfter && (
              <div {...(demoBeforeAfterTarget ? { 'data-demo-target': 'showcase-before-after' } : {})}>
                <div style={accordionRow(openReport)} onClick={() => setOpenReport(v => !v)}>
                  {/* Точка: серая — фото нет, зелёная — есть хотя бы одно */}
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: reportCount > 0 ? 'var(--green)' : 'var(--text3)', flexShrink: 0 }} />
                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', flex: 1 }}>{t('photos.beforeAfter')}</span>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>{reportCount}/{reportTotal}</span>
                  <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 6 }}>{openReport ? '▲' : '▼'}</span>
                </div>
                {openReport && (
                  <div style={{ paddingLeft: 4, paddingBottom: 8 }}>
                    {accordionBody(<>
                    <div style={{ display: 'flex', gap: 10, flexDirection: 'row', marginTop: 8, alignItems: 'flex-start' }}>
                      <ReportPhotoGallery
                        photos={beforePhotos}
                        setPhotos={setBeforePhotos}
                        label={t('photos.beforeWork')}
                        accentColor="var(--text3)"
                        onUpload={handleBeforeFiles}
                        onDelete={handleDelete}
                        onCaptionSave={handleCaptionSave}
                        uploading={uploadingBefore}
                        onReorder={handleReorder}
                        twoCol={true}
                        editMode={beforeEditMode}
                        onEditModeChange={setBeforeEditMode}
                        photoGroups={photoGroupsApi}
                        onGroupAssign={handleGroupAssign}
                        visibleGroupIds={visibleGroupIds}
                        onGroupsActivated={activateGroupIds}
                      />
                      <div style={{ width: 1, background: 'var(--border)', alignSelf: 'stretch', flexShrink: 0 }} />
                      <ReportPhotoGallery
                        photos={afterPhotos}
                        setPhotos={setAfterPhotos}
                        label={t('photos.afterWork')}
                        accentColor="var(--green)"
                        onUpload={handleAfterFiles}
                        onDelete={handleDelete}
                        onCaptionSave={handleCaptionSave}
                        uploading={uploadingAfter}
                        onReorder={handleReorder}
                        twoCol={true}
                        editMode={afterEditMode}
                        onEditModeChange={setAfterEditMode}
                        photoGroups={photoGroupsApi}
                        onGroupAssign={handleGroupAssign}
                        visibleGroupIds={visibleGroupIds}
                        onGroupsActivated={activateGroupIds}
                      />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10 }}>
                      {t('photos.photoHint', { max: MAX_BEFORE_AFTER })}
                    </div>
                    </>)}
                  </div>
                )}
              </div>
              )}

        </div>
    </div>
  );
}
